<template>
  <div class="my-account-login">
    <consent-dialog-fi
      ref="consentDialog"
      :fields="fields"
      @confirm="redirectTo(successRedirect)"
    />

    <component-wrapper v-if="autodetect === true">
      <country-selection-list-fi
        only-emit-on-click
        @country-clicked="countryClicked"
      />
    </component-wrapper>
    <site-login-standalone
      v-else
      :autodetect
      @logged-in="loggedInStandalone"
    />
  </div>
</template>

<script>
import ComponentWrapper from 'components/component-wrapper/ComponentWrapper.vue';
import CountrySelectionListFi from 'components/country-selection-list-fi/CountrySelectionListFi.vue';
import ConsentDialogFi from 'components/my-account/site-login/consent-dialog-fi/ConsentDialogFi';
import SiteLoginStandalone from 'components/my-account/site-login/SiteLoginStandalone';
import { useCountryList } from 'composables/countryList';
import GIVECONSENT from 'gql/giveConsent.gql';
import consentMixin from 'mixins/consentMixin';
import { ref, watchEffect } from 'vue';

import { getServices } from '@/components/fixperience/getLink';
import { useFischer } from '@/composables/fischerPlugin';

export default {
  name: 'MyAccountLogin',
  components: {
    ComponentWrapper,
    ConsentDialogFi,
    CountrySelectionListFi,
    SiteLoginStandalone,
  },
  mixins: [consentMixin],
  inject: ['fields'],
  setup() {
    const fischer = useFischer();
    const autodetect = ref(
      fischer.settings()?.shop?.clientIdOverwrite === 'fiwe-zz-fixperience' || IS_SHOWROOM,
    );
    const manualLanguage = fischer.query('manual-language') === 'true';
    const { countryList, loadCountryList, countryListLoaded } = useCountryList();
    if (manualLanguage) {
      autodetect.value === fischer.languageIsoCode();
    } else if (autodetect.value && !IS_SSR) {
      if (!IS_SSR) {
        loadCountryList();
      }

      watchEffect(() => {
        if (!countryListLoaded.value) {
          return;
        }

        const list = countryList.value?.countryList;
        const userLocale = countryList.value?.user;
        if (!list?.length) {
          if (!IS_SSR) {
            window.countryList = countryList.value;
          }
          return;
        }

        const detectedCountry =
          userLocale && list.find((country) => country.Value === userLocale.Value);

        if (userLocale) {
          const detectedLocale = detectedCountry?.Localizations?.find(
            (locale) => locale.Language?.Name === userLocale.Language?.Name,
          );
          const detectedIso = detectedLocale?.Iso_Code;
          if (!detectedCountry || !detectedIso) {
            console.error(
              'could not find country/locale for detected locale',
              JSON.stringify(userLocale),
            );
            return;
          }

          autodetect.value = detectedIso;
        }
      });
    }

    return { autodetect, countryListLoaded, countryList };
  },
  async created() {
    if (IS_SSR) {
      return;
    }

    // login will fail (CORS policy) if we are on an unsecure connection
    if (window.location.protocol !== 'https:' && !IS_SHOWROOM) {
      window.location.replace(
        `https:${window.location.href.substring(window.location.protocol.length)}`,
      );
      return;
    }

    const redirect = this.$fischer.query('redirect');
    if (!redirect) {
      return;
    }
    if (this.$fischer.user.authenticated()) {
      this.redirectTo(redirect);
      return;
    }
  },
  methods: {
    isCn() {
      return this.$fischer.siteLanguage() === 'zh-CN';
    },
    countryClicked(_country, locale) {
      this.autodetect = locale?.iso_Code || false;
    },
    async loggedInStandalone(el) {
      const newConsent = await this.checkNewConsents(el.accessToken);
      let { redirect } = el;
      const separator = redirect.includes('#') ? '&' : '#';
      if (el.data) {
        redirect = `${redirect}${separator}${el.data}&logged-in=true`;
      } else {
        redirect = `${redirect}${separator}logged-in=true`;
      }

      if (newConsent) {
        this.successRedirect = redirect;
      } else {
        this.redirectTo(redirect);
      }
    },
    async checkNewConsents(accessToken) {
      if (!this.fields.enableConsentDialog?.value) {
        return false;
      }

      try {
        const userConsentTemplates = await this.getConsentTemplates(accessToken);
        const registrationConsents = userConsentTemplates.filter(
          (consent) =>
            consent.id === `REGISTRATION_DATA_PRIVACY_${this.$fischer.baseSiteId()}` ||
            consent.id === `REGISTRATION_TERMS_AND_CONDITIONS_${this.$fischer.baseSiteId()}`,
        );

        let newConsent = false;
        registrationConsents.forEach((consent) => {
          if (!consent.currentConsent) {
            newConsent = true;
            this.newConsentTemplates.push({ id: consent.id, version: consent.version });
          }
        });

        if (newConsent) {
          this.$refs.consentDialog.open();
          this.acceptConsent(accessToken);
        }

        return newConsent;
      } catch (error) {
        console.error(error);
        return false;
      }
    },
    async acceptConsent(accessToken) {
      await this.newConsentTemplates.forEach(async (consentTemplate) => {
        await this.$apollo.mutate({
          mutation: GIVECONSENT,
          variables: {
            baseSiteId: this.$fischer.baseSiteId(),
            accessToken,
            userId: 'current',
            consentTemplateId: consentTemplate.id,
            consentTemplateVersion: consentTemplate.version,
            lang: this.$fischer.language_639_1(),
          },
        });
      });
    },
    isValidRedirect(url) {
      if (!url) {
        return false;
      }

      if (url.startsWith('/') || url.startsWith(window.location.origin)) {
        return true;
      }

      if (this.$fischer.settings()?.shop?.clientIdOverwrite !== 'fiwe-zz-fixperience') {
        return false;
      }
      if (IS_SHOWROOM && url.startsWith('/fixperience')) {
        return true;
      }
      if (APP_ENVIRONMENT === 'UAT') {
        if (/^https?:\/\/(?:localhost:[0-9]|[a-zA-Z0-9_.-]+\.fixperience\.online)/.test(url)) {
          return true;
        }
      }
      const services = getServices(this.isCn());
      return Object.values(services).some((serviceUrl) => url.startsWith(serviceUrl));
    },
    redirectTo(url) {
      if (this.isValidRedirect(url)) {
        window.location.href = url;
      } else {
        console.error('could not redirect', url);
        window.location.href = '/';
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import './my-account-login';
</style>
